import sequence1 from './sequence-1.png'
import sequence2 from './sequence-2.png'

const content = `<p>Information expert is one of the GRASP design principles. It helps decide where to assign responsibilities in code in a way that creates <a href="/high-cohesion">High cohesion</a> and <a href="/low-coupling">Low coupling</a>.</p>

<p>Information expert says that, in doubt, the object that should take on a responsibility in the code is the one that has the data to accomplish this responsibility.</p>

<p>It increases cohesion by grouping the behaviour with its related data, and reduces coupling by limiting interactions between objects asking for data to one another.</p>

<p>A heuristic that sums up this principle is “Choose the design that has the least arrows”.</p>

<p>Information expert comes with a caveat though: applying it repeatedly to add features to the same class makes it lose its cohesion. Information expert indicates which of the existing classes or modules is the best candidate to host the new code, but it doesn’t tell you when a class becomes too big. It has to be balanced with <a href="/high-cohesion">High cohesion</a>.</p>

<h2 id="Example">Example</h2>

<p>Let’s take an example to illustrate where Information expert can be useful. In a program that sends email to subscribers, the following class represents a subscriber:</p>

<pre><code class="language-ruby">class Subscriber
  def initialize(first_name, last_name)
    @first_name = first_name
    @last_name = last_name
  end

  def full_name
    @first_name + ' ' + @last_name
  end
end</code></pre>

<p>The program also has this function to compose an email to the subscriber:</p>

<pre><code class="language-ruby">def compose_email_to(subscriber)
  "Hello #{subscriber.full_name}, what's up?"
end</code></pre>

<p>Calling this function with a <code>Subscriber</code> like this:</p>

<pre><code class="language-ruby">compose_email_to(Subscriber.new("Arthur", "Dent"))</code></pre>

<p>produces the string <code>"Hello Arthur Dent, what's up?"</code>.</p>

<h2 id="Integrating-a-new-feature">Integrating a new feature</h2>

<p>Then comes a new requirement: subscribers should be able to have titles such as <code>"Mr."</code>, <code>"Mrs."</code>, <code>"Dr."</code>, and so on, and the titles should appear in the emails this way: <code>"Hello Mr. Arthur Dent, what's up?"</code>.</p>

<p>To implement this we first add a title to initialize <code>Subscriber</code>s with:</p>

<pre><code class="language-ruby">class Subscriber
  def initialize(first_name, last_name, title)
    @first_name = first_name
    @last_name = last_name
    @title = title
  end

  def full_name
    @first_name + ' ' + @last_name
  end
end</code></pre>

<p>Now we have one responsibility to add in the code: adding the title before the name of the subscriber, to obtain <code>"Mr. Arthur Dent"</code> instead of just <code>"Arthur Dent"</code>.</p>

<p>There are several places in the code that could handle that possibility. Among them:</p>

<ul><li>design option 1: the <code>Subscriber</code> class adds the title to the name</li><li>design option 2: the <code>compose_email_to</code> adds the title to the name</li></ul>

<p>Let’s examine those two options and use the Information expert principle to select one of them.</p>

<h3 id="Design-option-1:-responsibility-in-the-Subscriber-class">Design option 1: responsibility in the <code>Subscriber</code> class</h3>

<p>To make the <code>Subscriber</code> class handle the responsibility of adding the title, we can add a new <code>full_name_with_title</code> method to it (let's assume we can't change <code>full_name</code> because other contexts still need a full name without a title):</p>

<pre><code class="language-ruby">class Subscriber
  def initialize(first_name, last_name, title)
    @first_name = first_name
    @last_name = last_name
    @title = title
  end

  def full_name
    @first_name + ' ' + @last_name
  end
  
  def full_name_with_title
    @title + ' ' + full_name
  end
end</code></pre>

<p>Then <code>compose_email_to</code> calls this method:</p>

<pre><code class="language-ruby">def compose_email_to(subscriber)
  "Hello #{subscriber.full_name_with_title}, what's up?"
end</code></pre>

<p>In this design, <code>Subscriber</code> does not expose its title attribute.</p>

<h3 id="Design-option-2:-responsibility-in-the-compose_email_to-function">Design option 2: responsibility in the <code>compose_email_to</code> function</h3>

<p>In design option 2, it’s the <code>compose_email_to</code> function that adds the title, and it gets it from <code>Subscriber</code>. <code>Subscriber</code> exposes the <code>title</code> attribute:</p>

<pre><code class="language-ruby">class Subscriber
  def initialize(first_name, last_name, title)
    @first_name = first_name
    @last_name = last_name
    @title = title
  end
  
  attr_reader :title

  def full_name
    @first_name + ' ' + @last_name
  end
end</code></pre>

<p><code>compose_email_to</code> uses the title to add it before the name:</p>

<pre><code class="language-ruby">def compose_email_to(subscriber)
  "Hello #{subscriber.title} #{subscriber.full_name}, what's up?"
end</code></pre>

<h3 id="Choosing-a-design">Choosing a design</h3>

<p>Let’s compare the advantages and drawbacks of the two design options.</p>

<p>With design option 1, where <code>Subscriber</code> handles the responsibility:</p>

<ul><li><strong>advantage</strong>: <code>compose_email_to</code> is simple, with one function call</li><li><strong>drawbacks</strong>: <code>Subscriber</code> has more methods, and <code>compose_email_to</code> has no flexibility to format the title if needed later</li></ul>

<p>Conversely, with design option 2 where <code>compose_email_to</code> handles the responsibility:</p>

<ul><li><strong>advantages</strong>: <code>Subscriber</code> has simpler methods, and <code>compose_email_to</code> has more flexibility to format the title if needed later</li><li><strong>drawback</strong>: <code>compose_email_to</code> is more complex as it calls several functions and assembles the results</li></ul>

<p>How do we weigh those advantages and drawbacks? Is the flexibility to format the email differently valuable in case such a requirement comes later?</p>

<p>In such a case the Information expert principle guides us to pick one design. Information expert says that it’s the object that has the data that should do the job that uses that data.</p>

<p>Here it's <code>Subscriber</code> that knows the title, so <code>Subscriber</code> should handle the responsibility of adding it to the name. Information expert votes for design option 1.</p>

<p>In this simple example design option 1 is reasonable, but if <code>Subscriber</code> was already a big class, it would have made sense to choose a third design option: creating a new class to handle formatting. Information expert only says that design option 1 is better than design option 2.</p>

<h2 id="The-design-with-the-least-arrows">The design with the least arrows</h2>

<p>Information expert often means to choose the design with the least arrows. To see how that applies to our example, let’s draw the interactions between the parts of the code in both designs in sequence diagrams.</p>

<p>Design option 1: <code>Subscriber</code> handles the responsibility:</p>

<img src=${sequence1} alt="sequence1" />

<p>Design option 2: <code>compose_email_to</code> handles the responsibility</p>

<img src=${sequence2} alt="sequence2" />

<p>Information expert gives us a way to choose which part of the code the new feature would be the least detrimental to cohesion.</p>

<p>But applying this principle with new features repeatedly could make <code>Subscriber</code> grow and lose cohesion. To avoid this, we can apply Information expert inside of <code>Subscriber</code> as a guide to split it into several classes.</p>

<p>This example of Information expert is very local and is convenient to illustrate the principle. But the logic applies for lager components of code too, to decide which design to pick among several options.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Information expert means assigning responsibilities to classes that have the data to accomplish them</li><li>Information expert reduces coupling by limiting the number of queries between objects</li><li>Often it means to pick the design with the least arrows</li><li>It helps settle for a design among options that all have advantages and drawbacks</li><li>It applies at all scales in code</li></ul>
</div>`

export default content
